import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icons';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  sub?: string;
  // Footer slot — typically Cancel + the primary action. Rendered right-aligned with a
  // border separator above so it always sits at the bottom of the panel.
  footer?: ReactNode;
  // Optional max-width Tailwind class for the panel (defaults to a medium dialog width).
  panelClass?: string;
  children: ReactNode;
}

// Lightweight controlled modal with backdrop dismiss + escape-to-close. No portal because
// the app sits inside the AppShell at the document root and z-50 is enough; no focus trap
// because the editor isn't a high-stakes accessibility surface for v1 (admin-only).
//
// Reason for rolling our own rather than pulling in @radix-ui/react-dialog: the only
// extra value Radix gives us is focus trap + ARIA wiring, and we already have a working
// AlertDialog (Radix-backed) for confirmations. A plain modal for a one-off form keeps
// the dependency tree smaller; we can graduate to Radix's Dialog later if we add more.
export function Modal({ open, onClose, title, sub, footer, panelClass = 'max-w-xl', children }: ModalProps) {
  // Escape-to-close — wired only while the modal is open so we don't poison the keymap
  // when the modal is closed.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    // Also lock body scroll so the page behind doesn't scroll when the user scrolls inside
    // the modal — common pattern for full-screen overlays.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      // backdrop-blur-sm + bg-ink/60 — the blur softens the page, the darker tint pulls
      // the eye to the panel so the modal reads as a clear foreground layer (40% felt
      // closer to "tinted glass"). Applies to every Modal use across the app.
      className="fixed inset-0 z-50 grid place-items-center bg-ink/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* stopPropagation on the panel so an interior click doesn't dismiss the modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={'flex max-h-[90vh] w-full flex-col overflow-hidden rounded border border-line bg-surface shadow-pop ' + panelClass}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id="modal-title" className="font-display text-[18px] leading-[1.1] text-ink">
              {title}
            </h2>
            {sub && <p className="mt-0.5 text-[12.5px] text-muted">{sub}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 flex-none place-items-center rounded-sm text-ink-2 hover:bg-surface-2 hover:text-ink"
            aria-label="Close"
          >
            <Icon.close className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-line bg-bg-2 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
